import React, { FC, memo, ReactNode, useCallback } from 'react';
import { Modal } from 'shared/ui/Modal';
import { Button } from 'shared/ui/Button';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { WarningPing } from 'shared/ui/WarningPing';
import { useFileUpload } from '../hooks/useFileUpload';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { getUploadModalState } from '../model/selectors/getUploadModalState';
import { uploadModalActions } from '../model/slice/uploadModalSlice';
import { Dragger } from 'shared/ui/Dragger';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { DatePicker } from 'shared/ui/DatePicker';
import { CLIENT_DATE_WITH_MINUTE_TIME_FORMAT } from 'shared/utils/constants';

interface UploadModalProps {
  onOk: () => Promise<void>;
  isLoading?: boolean;
}

export const UploadModal: FC<UploadModalProps> = memo((props) => {
  const { onOk, isLoading } = props;

  const { isOpened, title, description, files, allowedFormats, multiple, maxCount, draggerDescription, needSpicifyUploadDate } =
    useAppSelector(getUploadModalState);

  const { t } = useAppTranslation('common');

  const dispatch = useAppDispatch();

  const { isDesktop } = useScreenBreakpoints();

  const isEmptyList = !files || files.length === 0;

  const { handleFileChange, resetFile, setUploadDate } = useFileUpload();

  const closeModal = useCallback((): void => {
    dispatch(uploadModalActions.closeModal());

    resetFile();
  }, [dispatch, resetFile]);

  const actions = (): ReactNode => (
    <div className="flex space-x-3">
      <Button containerClassName="w-full" theme="secondary" isLoading={isLoading} onClick={closeModal}>
        {t('Cancel')}
      </Button>
      <Button containerClassName="w-full" isLoading={isLoading} onClick={onOk} isDisabled={isEmptyList}>
        {t('Upload')}
      </Button>
    </div>
  );

  return (
    <Modal width={isDesktop ? 980 : 380} isOpen={isOpened} onClose={closeModal} closeIcon={false} footer={actions()}>
      <div className="mb-2">
        <WarningPing type="warning" />
      </div>
      <div className="font-semibold text-xl mb-3">{title}</div>
      <div className="text-primaryLight font-normal">{description}</div>
      {needSpicifyUploadDate && (
        <div className="flex flex-col items-center desktop:flex-row gap-3 w-fit my-3">
          <div className="text-primaryLight font-normal">{t('Need to specify the upload date?')}</div>
          <DatePicker
            className="h-[40px] w-full"
            format={CLIENT_DATE_WITH_MINUTE_TIME_FORMAT}
            inputReadOnly
            showTime
            placeholder={t('Enter upload date')}
            onChange={setUploadDate}
          />
        </div>
      )}

      <div className="my-3">
        <Dragger
          onChange={handleFileChange}
          description={draggerDescription}
          allowedFormats={allowedFormats}
          file={files}
          multiple={multiple}
          maxCount={maxCount || 1}
        />
      </div>
    </Modal>
  );
});
